import React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';

import { carryPreload, lazyChunk } from '../../../hoc/withLazyLoading';
import { PLANT_EQUIPMENT_PATTERNS, PLANT_EQUIPMENT_ROUTES, PLANT_EQUIPMENT_SEGMENTS, USER_CENTER_ROUTES } from '../../../navigation/paths';
import { createCrumbHandle, withCrumbParents } from '../../../utilities/auth';
import type { CrumbParent } from '../../../utilities/auth';
import { EQUIPMENT_LIBRARY_RULES, access, param } from '../../../access';
import { warmUp } from '../../warmRoutes';
import { plantTab } from '../../../modules/ctl-config/components/plant/plantTab';
import { warmMachine, warmMachines, warmScadaSync } from '../../../modules/ctl-config/routes/routeWarmUps';
import { guardedRoute } from '../guardedRoute';

import { CTL_CONFIG_FEATURE as FEATURE } from './feature';

const plantChunk = lazyChunk(() => import('../../../modules/ctl-config/routes/plantPages'));

const PlantEquipmentLayout = plantChunk((m) => m.PlantEquipmentLayout);

const MachinesTab = plantChunk((m) => m.MachinesTab);

const MachineDetailPane = plantChunk((m) => m.MachineDetailPane);

const SyncTab = plantChunk((m) => m.SyncTab);

const KeyedPlantEquipment: React.FC = () => {
  const { plantId = '' } = useParams();
  return guardedRoute(FEATURE, <PlantEquipmentLayout key={plantId} plantId={plantId} />, [plantId]);
};

const KeyedMachineDetail: React.FC = () => {
  const { plantId = '', entryId = '' } = useParams();
  const identity = `${plantId}/${entryId}`;
  return guardedRoute(FEATURE, <MachineDetailPane key={identity} plantId={plantId} entryId={entryId} />, [plantId, entryId]);
};

const SyncRedirect: React.FC = () => {
  const { plantId = '' } = useParams();
  return <Navigate to={PLANT_EQUIPMENT_ROUTES.sync(plantId)} replace />;
};

const GuardedPlantEquipment = carryPreload(KeyedPlantEquipment, PlantEquipmentLayout);

const GuardedMachineDetail = carryPreload(KeyedMachineDetail, MachineDetailPane);

const PLANTS_CRUMB: CrumbParent = { label: 'Plants & modules', pathname: USER_CENTER_ROUTES.plants };

const AT_PLANT = access(EQUIPMENT_LIBRARY_RULES.plant, param('plantId'));

export const plantEquipmentRoutes: RouteObject[] = [
  {
    path: PLANT_EQUIPMENT_PATTERNS.machines,
    element: <GuardedPlantEquipment />,
    handle: { ...withCrumbParents(createCrumbHandle('Equipment'), [PLANTS_CRUMB]), ...AT_PLANT },
    children: [
      {
        element: <MachinesTab />,
        handle: { ...plantTab('machines'), ...AT_PLANT },
        children: [
          { index: true, handle: { ...warmUp(warmMachines), ...AT_PLANT } },
          {
            path: ':entryId',
            element: <GuardedMachineDetail />,
            handle: { ...createCrumbHandle('Machine'), ...warmUp(warmMachine), ...AT_PLANT },
          },
        ],
      },
      {
        path: PLANT_EQUIPMENT_SEGMENTS.sync,
        element: <SyncTab />,
        handle: { ...createCrumbHandle('SCADA sync'), ...plantTab('sync'), ...warmUp(warmScadaSync), ...AT_PLANT },
      },
    ],
  },
  {
    path: PLANT_EQUIPMENT_PATTERNS.legacyScada,
    element: <SyncRedirect />,
    handle: AT_PLANT,
  },
];
